<script setup lang="ts">
import { inject, onMounted, ref, useAttrs, useTemplateRef } from "vue";

import { labelInputKey } from "./injectKeys";

const allowedChars: Record<string, string> = { int: "0123456789", float: ".0123456789" };

const props = defineProps<{
  value: string;
  maxLength: number;
  readonly?: boolean;
  placeholder?: string;
  underline?: boolean;
  type?: string;
}>();

const emit = defineEmits<{ input: [string]; change: [string] }>();

const labelInput = inject(labelInputKey, undefined);

defineExpose({ init });

const inputRef = useTemplateRef("input");

const edit = ref(false);
let initVal = "";

onMounted(() => {
  const attrs = useAttrs();
  labelInput?.setId(attrs.id as string);
});

function init() {
  if (!props.readonly) {
    edit.value = true;
    initVal = props.value;
    inputRef.value!.focus();
    inputRef.value!.setSelectionRange(0, ("" + props.value).length);
  }
}

function down(e: KeyboardEvent) {
  const elem = inputRef.value!;
  // key can be undefined when using autofill
  if (!e.key) {
    return;
  }
  if (e.key === "Escape") {
    elem.value = initVal;
    elem.blur();
  }
  if (e.key === "Enter") {
    elem.blur();
    e.stopPropagation();
  }
  if (e.key.length === 1) {
    const allowed = allowedChars[props.type || ""];
    if (allowed && !allowed.includes(e.key)) {
      e.preventDefault();
    }
    const hasSelection = (elem.selectionEnd || 0) - (elem.selectionStart || 0) > 0;
    const noMoreInput = elem.value.length >= props.maxLength && !hasSelection;
    if (props.type === "float") {
      const hasDot = elem.value.includes(".");
      if ((e.key === "." && hasDot) || (e.key !== "." && !hasDot && noMoreInput)) {
        e.preventDefault();
      }
    } else {
      if (noMoreInput) {
        e.preventDefault();
      }
    }
  }
}

function update() {
  emit("input", inputRef.value!.value);
}

function change() {
  emit("change", inputRef.value!.value);
}
</script>

<template>
  <input
    ref="input"
    :placeholder
    :maxlength="maxLength + 1"
    :value
    :readonly
    class="input-field"
    :class="{ edit, underline, error: !!labelInput?.error.value }"
    @click="init"
    @blur="edit = false"
    @input="update"
    @change="change"
    @keydown="down"
  />
</template>

<style lang="scss">
@use "@/styles/variables";
@use "@/styles/variables/colors";

.input-field {
  width: 100%;
  background-color: colors.$background-ui;
  color: inherit;
  border: 0.1em solid colors.$border-subtle;
  border-radius: variables.$border-radius;
  padding: 0.7em 1em;

  &:focus {
    border-color: colors.$border-focused;
    outline: none;
  }

  &.underline {
    background-color: transparent;
    border: none;
    border-bottom: 0.1em solid colors.$border-subtle;
    border-radius: 0;
    padding: 0.5em 0;
  }

  &.edit {
    background-color: colors.$background-ui;
  }

  &.error {
    border-color: colors.$destructive;
    color: colors.$destructive;
  }
}
</style>
